import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import { useSelector } from 'react-redux';
import { getMonthList } from '../../services/monthList';
import { getYearList } from '../../services/yearList';
import { getCompanyDisplayFromDetails, getShortCompanyName, getStoredCompanyDetails } from '../../services/companyService';
import Notification from '../Notification';
import StipendPayrollResultsTable from '../table-data/StipendPayrollResultsTable';

const monthOptions = getMonthList();
const yearOptions = getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues = {
  month: currentMonthName,
  year: currentYear,
};

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function CombinedStipendReport({ isEmbedded = false }) {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [results, setResults] = useState([]);
  const [totalDays, setTotalDays] = useState(null);
  const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
  const { companyName: globalCompanyName, addressLine1: globalAddressLine1, addressLine2: globalAddressLine2 } =
    getCompanyDisplayFromDetails(companyDetails || {});

  const canShowTable = useMemo(() => Array.isArray(results) && results.length > 0, [results]);

  const validate = () => {
    const temp = {};
    temp.month = values.month ? '' : 'This field is required.';
    temp.year = values.year ? '' : 'This field is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDisplay = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = { month: values.month, year: Number(values.year) };
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/combined-stipend-report`,
        payload
      );

      // Support both shapes:
      // 1) { results: [...] , total_days: n }
      // 2) [...] (array)
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.results || data?.data || [];
      setResults(list);
      setTotalDays((!Array.isArray(data) && data?.total_days) || null);
    } catch (e) {
      setResults([]);
      setTotalDays(null);
      setNotify({
        isOpen: true,
        message: e.response?.data?.message || 'Failed to load combined stipend report',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!Array.isArray(results) || results.length === 0) {
      setNotify({ isOpen: true, message: 'No data available to download!', type: 'error' });
      return;
    }

    const getName = (row) => {
      if (row?.name) return row.name;
      const first = row?.first_name || '';
      const last = row?.last_name || '';
      const combined = `${first} ${last}`.trim();
      return combined || '-';
    };

    const getDaysWorked = (row) => {
      if (row?.total_worked_days != null) return row.total_worked_days;
      if (row?.present_days != null) return row.present_days;
      return '-';
    };

    const numCols = 7;
    const excelData = [];

    // Header rows (similar style to other reports)
    excelData.push([globalCompanyName || 'Company', '', '', '', '', '', '']);
    excelData.push([globalAddressLine1 || '', '', '', '', '', '', '']);
    excelData.push([globalAddressLine2 || '', '', '', '', '', '', '']);
    excelData.push([`Combined Stipend Report for ${values.month} ${values.year}`, '', '', '', '', '', '']);
    // Spacer row (requested)
    excelData.push(['', '', '', '', '', '', '']);
    excelData.push(['Sr No', 'Intern/Apprentice Code', 'Name', 'Days Worked', 'Total Days', 'Stipend Amount', 'Net Pay']);

    results.forEach((row, index) => {
      excelData.push([
        index + 1,
        row.employee_code || '-',
        getName(row),
        getDaysWorked(row),
        row.total_days ?? totalDays ?? '-',
        row.stipend_amount != null ? Math.round(Number(row.stipend_amount)) : '',
        row.prorated_pay != null ? Math.round(Number(row.prorated_pay)) : '',
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Column widths
    ws['!cols'] = [
      { wch: 8 },
      { wch: 22 },
      { wch: 28 },
      { wch: 14 },
      { wch: 12 },
      { wch: 16 },
      { wch: 14 },
    ];

    // Merge first 4 rows across all columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } },
    ];

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };

    const headerTitleStyle = {
      font: { bold: true, sz: 14, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderStyle,
    };

    for (let row = 0; row <= 3; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[cellAddress]) ws[cellAddress].s = headerTitleStyle;
      for (let col = 1; col < numCols; col++) {
        const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[emptyCellAddress]) delete ws[emptyCellAddress];
      }
    }

    const tableHeaderStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '212529' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderStyle,
    };

    const dataStyle = {
      border: borderStyle,
      alignment: { vertical: 'center', horizontal: 'center' },
    };

    // Style table header row (row index 5)
    for (let col = 0; col < numCols; col++) {
      const cell = XLSX.utils.encode_cell({ r: 5, c: col });
      if (ws[cell]) {
        ws[cell].s = {
          ...tableHeaderStyle,
          alignment: {
            ...tableHeaderStyle.alignment,
            horizontal: col === 2 ? 'left' : 'center',
          },
        };
      }
    }

    // Style data rows
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let r = 6; r <= range.e.r; r++) {
      for (let c = 0; c < numCols; c++) {
        const cell = XLSX.utils.encode_cell({ r, c });
        if (ws[cell]) {
          ws[cell].s = {
            ...dataStyle,
            alignment: {
              ...dataStyle.alignment,
              horizontal: c === 2 ? 'left' : (c >= 5 ? 'right' : 'center'),
            },
          };
          if (c >= 5) {
            ws[cell].z = '#,##0';
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Combined Stipend');
    const reportCompanyName = getShortCompanyName(globalCompanyName) || 'Report';
    const filename = `${reportCompanyName} Stipend Report ${values.month.substring(0, 3)} ${values.year}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: isEmbedded ? '10px 20px' : '30px' }}>
      <Paper elevation={0} sx={{ width: '1000px' }}>
        <Stack spacing={2} sx={{ margin: isEmbedded ? '10px 10px' : '30px' }}>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <FormControl variant="outlined" sx={{ width: '200px' }} {...(errors.month && { error: true })}>
              <InputLabel>
                <RequiredLabel label="Month" />
              </InputLabel>
              <Select label="Month" name="month" size="small" value={values.month} onChange={handleInputChange}>
                <MenuItem value="">None</MenuItem>
                {monthOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
            </FormControl>

            <FormControl variant="outlined" sx={{ width: '200px' }} {...(errors.year && { error: true })}>
              <InputLabel>
                <RequiredLabel label="Year" />
              </InputLabel>
              <Select label="Year" name="year" size="small" value={values.year} onChange={handleInputChange}>
                <MenuItem value="">None</MenuItem>
                {yearOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
            </FormControl>

            <Button
              style={{ height: 40 }}
              variant="outlined"
              color="secondary"
              onClick={handleDisplay}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="secondary" /> : 'Display'}
            </Button>

            <Button
              style={{ height: 40 }}
              variant="contained"
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!canShowTable}
            >
              Download Excel
            </Button>
          </Stack>

          {canShowTable ? (
            <Box sx={{ overflowX: 'auto' }}>
              <StipendPayrollResultsTable results={results} totalDays={totalDays} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              No data to display.
            </Typography>
          )}
        </Stack>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default CombinedStipendReport;

